import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Flex } from '@chakra-ui/react';
import { EStructureItem, hasValue, useT } from '@progress-fe/core';
import { Svg } from '../../../../base';
const SCROLL_DELAY_MS = 300;
const NODE_HEIGHT_PX = 32;
const LEFT_OFFSET_PX = 8;
const TreeNodeFC = ({ item, title, depth, arrow, context, children, treeIcon, scrollRef, mainNodeId, wasScrolled, activeItems = [], isAlwaysExpanded, isOnlyMainExpandable, isClickOnActiveDisabled, onUpdateWasScrolled, onClickTreeItem }) => {
    const ref = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const { t } = useT();
    const data = useMemo(() => {
        return item.data;
    }, [item.data]);
    const hasDescription = useMemo(() => {
        return hasValue(data.description);
    }, [data.description]);
    const isIconShown = useMemo(() => {
        return depth === 0 && hasValue(treeIcon);
    }, [depth, treeIcon]);
    const isActive = useMemo(() => {
        return activeItems.some((r) => r.jsonPath === data.path);
    }, [data.path, activeItems]);
    const isNodeHoverable = useMemo(() => {
        return (!item.isFolder ||
            (!isAlwaysExpanded && !isOnlyMainExpandable) ||
            (isOnlyMainExpandable && item.index === mainNodeId));
    }, [isAlwaysExpanded, isOnlyMainExpandable, item.index, item.isFolder, mainNodeId]);
    useEffect(() => {
        if (ref.current && scrollRef?.current && isActive && !wasScrolled && !!onUpdateWasScrolled) {
            onUpdateWasScrolled(true);
            setTimeout(() => {
                if (scrollRef.current && ref.current) {
                    const offsetTop = ref.current.offsetTop - scrollRef.current.offsetTop;
                    scrollRef.current.scrollTo({
                        top: offsetTop - depth * (NODE_HEIGHT_PX + 1),
                        behavior: 'smooth'
                    });
                }
            }, SCROLL_DELAY_MS);
        }
    }, [isActive, wasScrolled, depth, scrollRef, onUpdateWasScrolled]);
    const getEntitySuffix = useCallback(() => {
        if (data.entityType === EStructureItem.Element) {
            return t(`enum.element.${data.entitySubType}`);
        }
        else if (data.entityType === EStructureItem.LogicalElement) {
            return t(`enum.logicalElement.${data.entitySubType}`);
        }
        else {
            return null;
        }
    }, [data.entitySubType, data.entityType, t]);
    const getStickyCss = useMemo(() => {
        return {
            top: item.isFolder ? `${depth * NODE_HEIGHT_PX + depth}px` : undefined,
            position: item.isFolder ? 'sticky' : undefined,
            zIndex: depth === 0 ? 1 : undefined
        };
    }, [depth, item.isFolder]);
    return (_jsxs("li", { ...context.itemContainerWithChildrenProps, style: {
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
        }, children: [_jsx("div", { ...context.itemContainerWithoutChildrenProps, ...context.interactiveElementProps, style: {
                    ...getStickyCss,
                    width: '100%',
                    borderBottom: '1px solid white'
                }, children: _jsxs(Box, { ref: ref, gap: '6px', display: 'flex', alignItems: 'center', h: `${NODE_HEIGHT_PX}px`, color: isActive ? 'white' : undefined, bg: depth === 0 ? 'buttonBg' : isActive ? 'accent !important' : 'bg', pl: `${LEFT_OFFSET_PX * depth}px`, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), _hover: {
                        cursor: isNodeHoverable ? 'pointer' : 'inherit',
                        bg: depth === 0 ? 'buttonBg' : isNodeHoverable && !item.isFolder ? 'accent10' : 'bg'
                    }, onClick: () => {
                        if (!item.isFolder && (!isActive || (isActive && !isClickOnActiveDisabled))) {
                            onClickTreeItem?.(item.data);
                        }
                    }, children: [_jsxs(Box, { w: '26px', pl: '8px', children: [isIconShown && _jsx(_Fragment, { children: treeIcon }), isActive && (_jsxs(_Fragment, { children: [!isHovered && _jsx(Svg, { name: 'Link' }), isHovered && isClickOnActiveDisabled && _jsx(Svg, { name: 'Link' }), isHovered && !isClickOnActiveDisabled && _jsx(Svg, { name: 'Cross', size: 's12' })] })), !isIconShown && !isOnlyMainExpandable && !isAlwaysExpanded && !isActive && (_jsx(_Fragment, { children: arrow }))] }), _jsxs(Flex, { w: '100%', pr: '12px', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', children: [_jsxs(Box, { fontWeight: item.isFolder ? 600 : 'normal', children: [_jsx(Box, { lineHeight: '14px', children: title }), hasDescription && (_jsx(Box, { fontSize: '10px', lineHeight: '10px', color: 'contrastIcon', className: 'cut-text', children: data.description }))] }), hasValue(data.entityType) && _jsx(Box, { color: 'accent', children: getEntitySuffix() }), (hasValue(data.value) || hasValue(data.units) || isActive) && (_jsx(Flex, { alignItems: 'center', gap: '4px', children: _jsxs(Box, { color: isActive ? 'white' : 'contrastIcon', children: [data.value, " ", data.units] }) }))] })] }) }), children] }));
};
export const TreeNode = observer(TreeNodeFC);
//# sourceMappingURL=TreeNode.js.map